/**
 * Shared bits for the money screens. Quiet, warm, mobile-first — the tokens in
 * src/styles.css do the talking. Every state (loading / empty / error) has a
 * real component here so no screen has to improvise one.
 */
import type { ReactNode } from 'react';
import { currentPeriod, periodLabel, periodShortLabel, recentPeriods, shiftPeriod } from './dates';

export const inputClass =
  'w-full min-h-[44px] rounded-xl border border-border bg-surface px-3 py-2 text-ink ' +
  'placeholder:text-ink-faint focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/40';

export const labelClass = 'mb-1 block text-xs font-medium tracking-wide text-ink-soft';

export const buttonClass =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-sage px-4 py-2 ' +
  'font-medium text-white transition-colors hover:bg-sage/90 focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-sage/50 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-canvas disabled:cursor-not-allowed disabled:opacity-60';

export const quietButtonClass =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-border ' +
  'bg-surface px-3 py-2 text-sm text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50';

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-border bg-surface p-5 ${className}`}>{children}</div>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/** Month stepper + picker. Prev/next are 44px targets; the select is labelled. */
export function PeriodPicker({
  period,
  onChange,
}: {
  period: string;
  onChange: (period: string) => void;
}) {
  const options = recentPeriods(18, period);
  const now = currentPeriod();

  return (
    <div className="flex items-center gap-1 rounded-xl border border-border bg-surface p-1">
      <button
        type="button"
        onClick={() => onChange(shiftPeriod(period, -1))}
        aria-label={`Previous month, ${periodLabel(shiftPeriod(period, -1))}`}
        className="grid h-11 w-11 place-items-center rounded-lg text-ink-soft hover:bg-surface-sunk hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50"
      >
        <span aria-hidden="true">‹</span>
      </button>

      <label className="sr-only" htmlFor="period-select">
        Month
      </label>
      <select
        id="period-select"
        value={period}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[44px] rounded-lg border-0 bg-transparent px-2 text-center font-medium text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50"
      >
        {options.map((p) => (
          <option key={p} value={p}>
            {periodShortLabel(p)}
            {p === now ? ' (this month)' : ''}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={() => onChange(shiftPeriod(period, 1))}
        aria-label={`Next month, ${periodLabel(shiftPeriod(period, 1))}`}
        className="grid h-11 w-11 place-items-center rounded-lg text-ink-soft hover:bg-surface-sunk hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/50"
      >
        <span aria-hidden="true">›</span>
      </button>
    </div>
  );
}

export function LoadingCards({ count = 4 }: { count?: number }) {
  return (
    <div
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
      role="status"
      aria-label="Loading budget"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-card border border-border bg-surface p-5">
          <div className="h-4 w-24 animate-pulse rounded bg-surface-sunk" />
          <div className="mt-4 h-8 w-32 animate-pulse rounded bg-surface-sunk" />
          <div className="mt-4 h-2 w-full animate-pulse rounded-full bg-surface-sunk" />
          <div className="mt-4 h-3 w-40 animate-pulse rounded bg-surface-sunk" />
        </div>
      ))}
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export function LoadingRows({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2" role="status" aria-label="Loading transactions">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-card border border-border bg-surface p-4">
          <div className="h-4 w-40 animate-pulse rounded bg-surface-sunk" />
          <div className="mt-3 h-3 w-24 animate-pulse rounded bg-surface-sunk" />
        </div>
      ))}
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card className="border-over/30 bg-clay-soft">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-medium text-ink">
            <span aria-hidden="true">⚠ </span>
            Couldn't load this
          </p>
          <p className="mt-1 text-sm text-ink-soft">{message}</p>
        </div>
        <button type="button" onClick={onRetry} className={quietButtonClass}>
          Try again
        </button>
      </div>
    </Card>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <Card className="text-center">
      <p className="font-display text-lg font-semibold text-ink">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-ink-soft">{body}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </Card>
  );
}

/** A transient confirmation, announced to screen readers. */
export function Flash({ message }: { message: string | null }) {
  return (
    <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-20 px-4">
      {message && (
        <p className="mx-auto w-fit rounded-full border border-border bg-ink px-4 py-2 text-sm text-canvas shadow-lg">
          {message}
        </p>
      )}
    </div>
  );
}
